import { App, CollapseProps, Flex } from "antd";
import { FC, useCallback, useMemo } from "react";
import { CheckoutOrder, OrderType } from "tm-shared/types";

import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
} from "@/hooks/redux";
import { setCheckoutActiveCollapse } from "@/store/slices/ui";

import { CheckoutBody } from "../CheckoutBody";
import { CheckoutHeader } from "../CheckoutHeader";
import {
    CollapseBorderColor,
    StyledCACheckbox,
    StyledCACollapse,
} from "./Styled";

interface ICheckoutItem {
    checkoutOrder: CheckoutOrder;
    isChosen: boolean;
    onSelect: () => void;
    onReset: () => void;
}

export const CheckoutItem: FC<ICheckoutItem> = ({
    checkoutOrder,
    isChosen,
    onSelect,
    onReset,
}) => {
    const { notification } = App.useApp();

    const dispatch = useAppDispatch();

    const offlineMode = useOfflineModeSelector();
    const activeCollapse = useAppSelector((s) => s.ui.checkout.activeCollapse);

    const { closed, orderType, id: invoiceId } = checkoutOrder;

    const borderColor = useMemo<CollapseBorderColor>(() => {
        if (!invoiceId) return "offline";

        if (orderType === OrderType.H) return closed ? "closed" : "opened";

        return orderType;
    }, [invoiceId, orderType, closed]);

    const handleCheckboxChange = useCallback(() => {
        if (offlineMode) {
            notification.error({
                title: "В офлайн режимі неможливо здійснити оформлення замовлення!",
            });

            return;
        }

        onSelect();
    }, [notification, offlineMode, onSelect]);

    const handleCollapseChange = useCallback(
        (key: string | string[]) => {
            dispatch(
                setCheckoutActiveCollapse(
                    (Array.isArray(key) ? key[0] : key) ?? null,
                ),
            );
        },
        [dispatch],
    );

    const items: CollapseProps["items"] = [
        {
            key: checkoutOrder.orderId,
            label: <CheckoutHeader checkoutOrder={checkoutOrder} />,
            children: (
                <CheckoutBody checkoutOrder={checkoutOrder} onReset={onReset} />
            ),
        },
    ];

    return (
        <Flex align="flex-start">
            {!closed && (
                <StyledCACheckbox
                    onChange={handleCheckboxChange}
                    checked={isChosen}
                    $isActive={!offlineMode}
                />
            )}

            <StyledCACollapse
                accordion
                activeKey={activeCollapse ?? undefined}
                $borderColor={borderColor}
                onChange={handleCollapseChange}
                items={items}
            />
        </Flex>
    );
};
